import { useEffect, useState } from "react";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import CustomButton from "../../Components/CustomButton";
import CustomModal from "../../Components/CustomModal";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import Loader from "../../Components/Loader";
import CustomTable from "../../Components/CustomTable";
import ProfileDisplay from "../../Components/ProfileDisplay";
import usePagination from "../../Hooks/usePagination";
import useSearch from "../../Hooks/useSearch";
import {
  useGetForumPostsQuery,
  useGetForumCommentsQuery,
  useAdminDeletePostMutation,
  useAdminDeleteCommentMutation,
} from "../../Redux/Apis/Forum";
import {
  useGetReportsQueueQuery,
  useResolveReportMutation,
} from "../../Redux/Apis/Report";
import { dateFormatter } from "../../Utils";

// The 19th Hole forum gives every post a moderation status. These are the
// only three the backend ever sets, so this is the full universe of badges
// we render in the Status column. (There is no "flagged" status — that was a
// fiction in the first draft.)
//   active  → live and visible to residents
//   hidden  → taken down but recoverable
//   deleted → soft-deleted (kept in the DB so admins can still audit it)
const POST_STATUS_BADGE = {
  active: "bg-success",
  hidden: "bg-warning text-dark",
  deleted: "bg-danger",
};

// A report's moderation state. New reports come in as "pending"; once an admin
// deals with one it lands in "actioned" (we removed the content) or "dismissed"
// (we looked and there was nothing to remove). "reviewed" is a legacy/neutral
// state the backend may set. These drive the colored badge in the Status column.
const REPORT_STATUS_BADGE = {
  pending: "bg-warning text-dark",
  reviewed: "bg-info text-dark",
  actioned: "bg-success",
  dismissed: "bg-secondary",
};

// Friendly labels for what kind of thing got reported. The backend sends the
// raw model value (e.g. "forum_post"); this turns it into something an admin
// reads at a glance. Falls back to the raw value if we ever see a new kind.
const REPORTED_KIND_LABEL = {
  forum_post: "Post",
  forum_comment: "Comment",
  chat_message: "Chat message",
  user_profile: "User",
  vendor_profile: "Vendor",
};

// Which tab the admin is looking at: posts list, comments list, or reports queue
const TAB_POSTS = "posts";
const TAB_COMMENTS = "comments";
const TAB_REPORTS = "reports";

const ContentModeration = () => {
  const [activeTab, setActiveTab] = useState(TAB_POSTS);

  // ─── Posts tab state ───────────────────────────────────────────────────────
  const [showDeletePostModal, setShowDeletePostModal] = useState(false);
  const [showDeletePostSuccessModal, setShowDeletePostSuccessModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // ─── Comments tab state ────────────────────────────────────────────────────
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [showDeleteCommentSuccessModal, setShowDeleteCommentSuccessModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);

  // ─── Reports tab state ─────────────────────────────────────────────────────
  // We keep track of which report the admin is acting on AND which action they
  // picked, because the two row buttons ("Delete content" vs "Dismiss") share
  // one confirm modal — the action tells the handler what to send to the server.
  const [showResolveReportModal, setShowResolveReportModal] = useState(false);
  const [showResolveReportSuccessModal, setShowResolveReportSuccessModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  // "delete" = remove the reported content + close the report.
  // "dismiss" = close the report and leave the content alone.
  const [reportAction, setReportAction] = useState(null);

  // Each tab gets its own pagination + search so switching tabs keeps both
  // at the right position without resetting each other.
  const [search, debouncedSearch, onSearch] = useSearch();
  const { page, perPage, onNext, onPrevious } = usePagination();

  const [searchComments, debouncedSearchComments, onSearchComments] = useSearch();
  const {
    page: commentPage,
    perPage: commentPerPage,
    onNext: onNextComment,
    onPrevious: onPreviousComment,
  } = usePagination();

  // The Reports tab gets its own pagination too. It has no free-text search —
  // the queue is small and admins act on it top-to-bottom — so there's no
  // search box here, just paging.
  const {
    page: reportPage,
    perPage: reportPerPage,
    onNext: onNextReport,
    onPrevious: onPreviousReport,
  } = usePagination();

  // Fetch posts from the admin endpoint — this returns every post in the
  // neighborhood (active, hidden, deleted) so the moderation table is complete.
  // Note the param is `rowsPerPage`, not `limit` — that's the backend contract.
  const { data: postsData, isLoading: isLoadingPosts, isFetching: isFetchingPosts } =
    useGetForumPostsQuery({
      page,
      rowsPerPage: perPage,
      search: debouncedSearch,
    });

  // Fetch comments from their own admin endpoint. Comments are NOT embedded in
  // the posts payload, so we ask the server directly and let it paginate.
  // Only runs while the Comments tab is open so we don't fetch it needlessly.
  const { data: commentsData, isLoading: isLoadingComments, isFetching: isFetchingComments } =
    useGetForumCommentsQuery(
      {
        page: commentPage,
        rowsPerPage: commentPerPage,
        search: debouncedSearchComments,
      },
      { skip: activeTab !== TAB_COMMENTS }
    );

  // Fetch the reported-content queue. We don't pass `status`, so the backend
  // gives us its default view (actionable/pending reports — the things still
  // waiting on an admin). Only runs while the Reports tab is open.
  const { data: reportsData, isLoading: isLoadingReports, isFetching: isFetchingReports } =
    useGetReportsQueueQuery(
      {
        page: reportPage,
        rowsPerPage: reportPerPage,
      },
      { skip: activeTab !== TAB_REPORTS }
    );

  const [adminDeletePost, { isLoading: isDeletingPost }] = useAdminDeletePostMutation();
  const [adminDeleteComment, { isLoading: isDeletingComment }] = useAdminDeleteCommentMutation();
  const [resolveReport, { isLoading: isResolvingReport }] = useResolveReportMutation();

  useEffect(() => {
    document.title = "The Turn At | 19th Hole Moderation";
  }, []);

  // ─── Delete handlers ───────────────────────────────────────────────────────

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    try {
      await adminDeletePost(selectedPost._id).unwrap();
      setShowDeletePostModal(false);
      setShowDeletePostSuccessModal(true);
      setSelectedPost(null);
    } catch (e) {
      toast.error(e?.data?.message || "Failed to delete post. Please try again.");
      setShowDeletePostModal(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedComment) return;
    try {
      await adminDeleteComment(selectedComment._id).unwrap();
      setShowDeleteCommentModal(false);
      setShowDeleteCommentSuccessModal(true);
      setSelectedComment(null);
    } catch (e) {
      toast.error(e?.data?.message || "Failed to delete comment. Please try again.");
      setShowDeleteCommentModal(false);
    }
  };

  // Resolve a report. Both row buttons funnel through here; `reportAction` tells
  // us whether the admin chose to delete the content or just dismiss the report,
  // and we translate that into the body the backend expects:
  //   delete  → { action: 'resolved',  deleteContent: true  }
  //   dismiss → { action: 'dismissed', deleteContent: false }
  const handleResolveReport = async () => {
    if (!selectedReport || !reportAction) return;
    const deleteContent = reportAction === "delete";
    try {
      await resolveReport({
        id: selectedReport._id,
        action: deleteContent ? "resolved" : "dismissed",
        deleteContent,
      }).unwrap();
      setShowResolveReportModal(false);
      setShowResolveReportSuccessModal(true);
      setSelectedReport(null);
      setReportAction(null);
    } catch (e) {
      toast.error(e?.data?.message || "Failed to update this report. Please try again.");
      setShowResolveReportModal(false);
    }
  };

  // ─── Table column definitions ──────────────────────────────────────────────

  const postHeaders = [
    { key: "id", title: "S.No" },
    { key: "title", title: "Title" },
    { key: "author", title: "Author" },
    { key: "group", title: "Group" },
    { key: "date", title: "Date" },
    { key: "status", title: "Status" },
    { key: "actions", title: "Actions" },
  ];

  const commentHeaders = [
    { key: "id", title: "S.No" },
    { key: "content", title: "Comment" },
    { key: "author", title: "Author" },
    { key: "parentPost", title: "Post" },
    { key: "status", title: "Status" },
    { key: "date", title: "Date" },
    { key: "actions", title: "Actions" },
  ];

  // Columns give the admin everything needed to make a call without leaving the
  // row: who flagged it, what kind of thing it is, a snippet of the reported
  // content + its author, why it was flagged, when, and the current status.
  const reportHeaders = [
    { key: "id", title: "S.No" },
    { key: "reporter", title: "Reported By" },
    { key: "kind", title: "Type" },
    { key: "content", title: "Reported Content" },
    { key: "targetAuthor", title: "Content Author" },
    { key: "reason", title: "Reason" },
    { key: "date", title: "Date" },
    { key: "status", title: "Status" },
    { key: "actions", title: "Actions" },
  ];

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mainTitle">19th Hole Moderation</h2>
              </div>

              {/* Tab switcher — matches the pill-style tabs used elsewhere in the app */}
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === TAB_POSTS ? "active" : ""}`}
                    onClick={() => setActiveTab(TAB_POSTS)}
                  >
                    Posts
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === TAB_COMMENTS ? "active" : ""}`}
                    onClick={() => setActiveTab(TAB_COMMENTS)}
                  >
                    Comments
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === TAB_REPORTS ? "active" : ""}`}
                    onClick={() => setActiveTab(TAB_REPORTS)}
                  >
                    Reports
                  </button>
                </li>
              </ul>

              {/* ── POSTS TAB ─────────────────────────────────────────────── */}
              {activeTab === TAB_POSTS && (
                <div className="row mb-5">
                  <div className="col-12">
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: 320 }}
                        placeholder="Search posts..."
                        value={search}
                        onChange={onSearch}
                      />
                    </div>

                    {isLoadingPosts || isFetchingPosts ? (
                      <Loader />
                    ) : (
                      <CustomTable
                        headers={postHeaders}
                        length={postsData?.data?.length || 0}
                        page={page}
                        rowsPerPage={perPage}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        total={postsData?.total}
                      >
                        <tbody>
                          {(postsData?.data || []).map((item, index) => (
                            <tr key={item._id}>
                              <td>{(index + 1) + (page - 1) * perPage}</td>
                              <td>{item.title || <span className="text-muted">Untitled</span>}</td>
                              <td>
                                <ProfileDisplay
                                  name={item.author?.name || "Unknown"}
                                  image={item.author?.picture}
                                />
                              </td>
                              <td>
                                {item.groupId?.name ? (
                                  <span className="badge bg-info text-dark">{item.groupId.name}</span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>{dateFormatter(item.createdAt)}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    POST_STATUS_BADGE[item.status] || "bg-secondary"
                                  }`}
                                >
                                  {item.status || "active"}
                                </span>
                              </td>
                              <td>
                                <CustomButton
                                  variant="dangerButton"
                                  icon={faTrash}
                                  className="btn-sm"
                                  onClick={() => {
                                    setSelectedPost(item);
                                    setShowDeletePostModal(true);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                          {(postsData?.data || []).length === 0 && (
                            <tr>
                              <td colSpan={postHeaders.length} className="text-center py-4 text-muted">
                                No posts found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </CustomTable>
                    )}
                  </div>
                </div>
              )}

              {/* ── COMMENTS TAB ──────────────────────────────────────────── */}
              {activeTab === TAB_COMMENTS && (
                <div className="row mb-5">
                  <div className="col-12">
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: 320 }}
                        placeholder="Search comments..."
                        value={searchComments}
                        onChange={onSearchComments}
                      />
                    </div>

                    {isLoadingComments || isFetchingComments ? (
                      <Loader />
                    ) : (
                      <CustomTable
                        headers={commentHeaders}
                        length={commentsData?.data?.length || 0}
                        page={commentPage}
                        rowsPerPage={commentPerPage}
                        onNext={onNextComment}
                        onPrevious={onPreviousComment}
                        total={commentsData?.total}
                      >
                        <tbody>
                          {(commentsData?.data || []).map((item, index) => (
                            <tr key={item._id}>
                              <td>{(index + 1) + (commentPage - 1) * commentPerPage}</td>
                              <td style={{ maxWidth: 280 }}>
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={item.content}
                                >
                                  {item.content || <span className="text-muted">No content</span>}
                                </span>
                              </td>
                              <td>
                                <ProfileDisplay
                                  name={item.author?.name || "Unknown"}
                                  image={item.author?.picture}
                                />
                              </td>
                              <td>
                                <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                                  {item.postId?.title || "Untitled post"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    POST_STATUS_BADGE[item.status] || "bg-secondary"
                                  }`}
                                >
                                  {item.status || "active"}
                                </span>
                              </td>
                              <td>{dateFormatter(item.createdAt)}</td>
                              <td>
                                <CustomButton
                                  variant="dangerButton"
                                  icon={faTrash}
                                  className="btn-sm"
                                  onClick={() => {
                                    setSelectedComment(item);
                                    setShowDeleteCommentModal(true);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                          {(commentsData?.data || []).length === 0 && (
                            <tr>
                              <td colSpan={commentHeaders.length} className="text-center py-4 text-muted">
                                No comments found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </CustomTable>
                    )}
                  </div>
                </div>
              )}

              {/* ── REPORTS TAB ───────────────────────────────────────────── */}
              {activeTab === TAB_REPORTS && (
                <div className="row mb-5">
                  <div className="col-12">
                    {isLoadingReports || isFetchingReports ? (
                      <Loader />
                    ) : (
                      <CustomTable
                        headers={reportHeaders}
                        length={reportsData?.data?.length || 0}
                        page={reportPage}
                        rowsPerPage={reportPerPage}
                        onNext={onNextReport}
                        onPrevious={onPreviousReport}
                        total={reportsData?.total}
                      >
                        <tbody>
                          {(reportsData?.data || []).map((item, index) => (
                            <tr key={item._id}>
                              <td>{(index + 1) + (reportPage - 1) * reportPerPage}</td>

                              {/* Who tapped "Report" */}
                              <td>{item.reporterName || <span className="text-muted">Unknown</span>}</td>

                              {/* What kind of thing was reported (post/comment/user/etc) */}
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {REPORTED_KIND_LABEL[item.reportedType] ||
                                    item.targetKind ||
                                    item.reportedType ||
                                    "—"}
                                </span>
                              </td>

                              {/* A snippet of the reported content so the admin can see
                                  what they're acting on without opening anything else */}
                              <td style={{ maxWidth: 280 }}>
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={item.targetSnippet}
                                >
                                  {item.targetSnippet || (
                                    <span className="text-muted">No preview available</span>
                                  )}
                                </span>
                              </td>

                              {/* Who authored the reported content */}
                              <td>
                                {item.targetAuthorName || <span className="text-muted">—</span>}
                              </td>

                              {/* Why it was flagged (+ the free-text note if there is one) */}
                              <td>
                                <span className="text-capitalize">{item.reason || "—"}</span>
                                {item.details ? (
                                  <span
                                    className="d-block text-muted"
                                    style={{ fontSize: "0.75rem" }}
                                    title={item.details}
                                  >
                                    {item.details}
                                  </span>
                                ) : null}
                              </td>

                              <td>{dateFormatter(item.createdAt)}</td>

                              <td>
                                <span
                                  className={`badge ${
                                    REPORT_STATUS_BADGE[item.status] || "bg-secondary"
                                  }`}
                                >
                                  {item.status || "pending"}
                                </span>
                              </td>

                              {/* Two actions: remove the content, or clear the report */}
                              <td>
                                <div className="d-flex gap-2">
                                  <CustomButton
                                    variant="dangerButton"
                                    text="Delete content"
                                    className="btn-sm"
                                    onClick={() => {
                                      setSelectedReport(item);
                                      setReportAction("delete");
                                      setShowResolveReportModal(true);
                                    }}
                                  />
                                  <CustomButton
                                    variant="secondaryButton"
                                    text="Dismiss"
                                    className="btn-sm"
                                    onClick={() => {
                                      setSelectedReport(item);
                                      setReportAction("dismiss");
                                      setShowResolveReportModal(true);
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                          {(reportsData?.data || []).length === 0 && (
                            <tr>
                              <td colSpan={reportHeaders.length} className="text-center py-4 text-muted">
                                No reports to review.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </CustomTable>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Confirm: delete post ─────────────────────────────────────────── */}
        <CustomModal
          loading={isDeletingPost}
          show={showDeletePostModal}
          close={() => {
            setShowDeletePostModal(false);
            setSelectedPost(null);
          }}
          action={handleDeletePost}
          heading="Delete this post? This cannot be undone."
        />
        <CustomModal
          show={showDeletePostSuccessModal}
          close={() => setShowDeletePostSuccessModal(false)}
          success
          heading="Post deleted."
        />

        {/* ── Confirm: delete comment ──────────────────────────────────────── */}
        <CustomModal
          loading={isDeletingComment}
          show={showDeleteCommentModal}
          close={() => {
            setShowDeleteCommentModal(false);
            setSelectedComment(null);
          }}
          action={handleDeleteComment}
          heading="Delete this comment? This cannot be undone."
        />
        <CustomModal
          show={showDeleteCommentSuccessModal}
          close={() => setShowDeleteCommentSuccessModal(false)}
          success
          heading="Comment deleted."
        />

        {/* ── Confirm: resolve report ──────────────────────────────────────── */}
        {/* One modal, two outcomes. The heading changes based on which button the
            admin pressed so they know exactly what they're confirming. */}
        <CustomModal
          loading={isResolvingReport}
          show={showResolveReportModal}
          close={() => {
            setShowResolveReportModal(false);
            setSelectedReport(null);
            setReportAction(null);
          }}
          action={handleResolveReport}
          heading={
            reportAction === "delete"
              ? "Delete the reported content and close this report? This cannot be undone."
              : "Dismiss this report? The content stays up and the report is cleared."
          }
        />
        <CustomModal
          show={showResolveReportSuccessModal}
          close={() => setShowResolveReportSuccessModal(false)}
          success
          heading="Report updated."
        />
      </div>
    </DashboardLayout>
  );
};

export default ContentModeration;
